import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSignIn } from "@clerk/react";
import monitoring from "@/lib/monitoring";

export type ForgotStage = "send-code" | "verify-code" | "set-password" | "complete";
export type ForgotPending = "none" | "sending" | "verifying" | "submitting";

export function useForgotPasswordFlow(initialEmail = "") {
  const { signIn } = useSignIn();
  const navigate = useNavigate();

  const [stage, setStage] = useState<ForgotStage>("send-code");
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState<ForgotPending>("none");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const sendCode = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (pending !== "none") return;
    setFieldError(null);
    setFormError(null);

    const v = email.trim().toLowerCase();
    if (!v || !v.includes("@")) {
      setFieldError("Enter a valid work email.");
      return;
    }

    setPending("sending");
    try {
      const { error: createError } = await signIn.create({ identifier: v });
      if (createError) {
        setFormError(createError.longMessage || createError.message || "Couldn't start password reset.");
        setPending("none");
        return;
      }

      const { error: sendError } = await signIn.resetPasswordEmailCode.sendCode();
      if (sendError) {
        setFormError(sendError.longMessage || sendError.message || "Couldn't send reset code.");
        setPending("none");
        return;
      }

      setStage("verify-code");
    } catch (err) {
      monitoring.error(
        "forgot_password_send_code_threw",
        err instanceof Error ? err : new Error(String(err)),
      );
      setFormError(err instanceof Error ? err.message : "Couldn't send reset code.");
    } finally {
      setPending("none");
    }
  };

  const verifyCode = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (pending !== "none") return;
    setFieldError(null);
    setFormError(null);
    setPending("verifying");

    try {
      const { error } = await signIn.resetPasswordEmailCode.verifyCode({ code });
      if (error) {
        setFieldError(error.longMessage || error.message || "Invalid code.");
        setPending("none");
        return;
      }

      if (signIn.status === "needs_new_password") {
        setStage("set-password");
      } else {
        setFormError("Unexpected response. Please try again.");
      }
    } catch (err) {
      monitoring.error(
        "forgot_password_verify_code_threw",
        err instanceof Error ? err : new Error(String(err)),
      );
      setFormError(err instanceof Error ? err.message : "Couldn't verify code.");
    } finally {
      setPending("none");
    }
  };

  const submitPassword = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (pending !== "none") return;
    setFieldError(null);
    setFormError(null);
    setPending("submitting");

    try {
      const { error } = await signIn.resetPasswordEmailCode.submitPassword({
        password,
        signOutOfOtherSessions: true,
      });
      if (error) {
        setFieldError(error.longMessage || error.message || "Couldn't set password.");
        setPending("none");
        return;
      }

      if (signIn.status === "complete") {
        const { error: finalizeError } = await signIn.finalize({
          navigate: ({ decorateUrl }) => {
            const url = decorateUrl("/");
            if (url.startsWith("http")) {
              window.location.href = url;
            } else {
              navigate(url, { replace: true });
            }
          },
        });
        if (finalizeError) {
          setFormError(
            finalizeError.longMessage ||
              finalizeError.message ||
              "Password set but finalize failed.",
          );
        }
        setStage("complete");
      } else if (signIn.status === "needs_second_factor") {
        setFormError(
          "Additional verification is required to complete sign-in. Please contact support.",
        );
      } else {
        setFormError("Unexpected response. Please try again.");
      }
    } catch (err) {
      monitoring.error(
        "forgot_password_submit_threw",
        err instanceof Error ? err : new Error(String(err)),
      );
      setFormError(err instanceof Error ? err.message : "Couldn't set password.");
    } finally {
      setPending("none");
    }
  };

  return {
    stage,
    email,
    setEmail,
    code,
    setCode,
    password,
    setPassword,
    pending,
    fieldError,
    formError,
    sendCode,
    verifyCode,
    submitPassword,
  };
}

export type ForgotPasswordFlow = ReturnType<typeof useForgotPasswordFlow>;
